import React, { useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import { componentTypeRegistry } from '../../../../../organisms/ComponentBlueprint/componentTypeRegistry'

function ComponentDetailPanel ({
  code,
  data,
  onUpdateConfig,
  onUpdateOption
}) {
  const [configText, setConfigText] = useState('')
  const [configError, setConfigError] = useState(null)

  const typeEntry = useMemo(() => {
    return data?.componentType ? componentTypeRegistry[data.componentType] : null
  }, [data?.componentType])

  const ConfigEditor = typeEntry?.configEditor ?? null
  const styleVariantOptions = typeEntry?.styleVariants ?? null

  useEffect(() => {
    if (ConfigEditor) return
    if (data?.config) {
      setConfigText(JSON.stringify(data.config, null, 2))
      setConfigError(null)
    } else {
      setConfigText('{}')
      setConfigError(null)
    }
  }, [code, data?.config, ConfigEditor])

  const handleConfigBlur = useCallback(() => {
    try {
      const parsed = JSON.parse(configText)
      setConfigError(null)
      onUpdateConfig(code, parsed)
    } catch {
      setConfigError('Invalid JSON')
    }
  }, [configText, code, onUpdateConfig])

  const handleStyleVariantChange = useCallback((e) => {
    onUpdateOption(code, 'styleVariant', e.target.value)
  }, [code, onUpdateOption])

  const handleDisplayNameChange = useCallback((e) => {
    onUpdateOption(code, 'displayName', e.target.value)
  }, [code, onUpdateOption])

  const handleEditorConfigUpdate = useCallback((newConfig) => {
    onUpdateConfig(code, newConfig)
  }, [code, onUpdateConfig])

  const handleEditorStyleVariantUpdate = useCallback((value) => {
    onUpdateOption(code, 'styleVariant', value)
  }, [code, onUpdateOption])

  if (!code || !data) {
    return (
      <div style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--text-color-subtle)',
        paddingLeft: '32px'
      }}
      >
        <p>Select a component to view and edit details</p>
      </div>
    )
  }

  return (
    <div style={{ paddingLeft: '24px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '4px' }}>{data.displayName}</h3>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
          {data.componentType} &bull; {code}
        </div>
        {data.description
          ? (
            <p style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              {data.description}
            </p>
          )
          : null}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <SydLabel label='Display Name'>
          <SydInput
            value={data.displayName || ''}
            onChange={handleDisplayNameChange}
          />
        </SydLabel>
      </div>

      {ConfigEditor
        ? (
          <ConfigEditor
            config={data.config || {}}
            styleVariant={data.styleVariant}
            onUpdateConfig={handleEditorConfigUpdate}
            onUpdateStyleVariant={handleEditorStyleVariantUpdate}
          />
        )
        : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <SydLabel label='Style Variant'>
                {styleVariantOptions
                  ? (
                    <SydSelect
                      options={styleVariantOptions}
                      value={data.styleVariant || 'default'}
                      onChange={handleStyleVariantChange}
                    />
                  )
                  : (
                    <SydInput
                      value={data.styleVariant || 'default'}
                      onChange={handleStyleVariantChange}
                    />
                  )}
              </SydLabel>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <SydLabel label='Config (JSON)' error={configError}>
                <textarea
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                  onBlur={handleConfigBlur}
                  rows={15}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: configError ? '1px solid var(--color-danger-500)' : '1px solid var(--color-neutral-300)',
                    resize: 'vertical'
                  }}
                />
              </SydLabel>
            </div>
          </>
        )}
    </div>
  )
}

ComponentDetailPanel.propTypes = {
  code: PropTypes.string,
  data: PropTypes.object,
  onUpdateConfig: PropTypes.func.isRequired,
  onUpdateOption: PropTypes.func.isRequired
}

export default ComponentDetailPanel
